import { AfterViewChecked, ChangeDetectorRef, Component, HostListener, OnInit } from '@angular/core';
import { JwtUtilsService } from '../_service/jwt-utils.service';
import { EidasUser } from '../_model/eidasUser';
import { OotsService } from '../_service/oots.service';
import { ActivatedRoute } from '@angular/router';
import { OotsRequest } from '../_model/oots-request';
import { SessionTimerComponent } from '../_common/session-timer/session-timer.component';
import { StateService } from '../_service/state.service';
import { DocumentCounterService } from '../_service/document-counter.service';
import { BreadcrumbService } from '../_service/breadcrumb.service';
import { Observable, Subscription } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { SessionService } from '../_service/session.service';
import { OOTSRequestSession } from '../_model/oots-request-session';

@Component({
  selector: 'app-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.css']
})
export class HomeComponent implements OnInit, AfterViewChecked {

  private eventsSubscription = new Subscription();
  continuationToken: string;
  uuid: string;
  title = 'IntermediaryApp - Home';
  isUserLogged = false;
  currentUser: EidasUser;
  ootsRequest: OotsRequest;
  tokenFound = true;
  isRequestDraftStatus = true;
  requirementsList: string[] = [];
  procedureDescription: string;
  step = 1;
  recievedDocuments: number;
  totalDocuments: number;
  isChecked = false;
  regex = /^[A-Za-z]{2}\/[A-Za-z]{2}\//;
  loading: boolean = true;
  lang: string;
  existsPreviousSession: boolean = false;
  sessionData: OOTSRequestSession;

  constructor(
    private jwtUtilService: JwtUtilsService,
    private ootsService: OotsService,
    private route: ActivatedRoute,
    private sessionTimerComponent: SessionTimerComponent,
    private stateService: StateService,
    private documentCounterService: DocumentCounterService,
    private breadcrumbService: BreadcrumbService,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private sessionService: SessionService
  ) {
    this.eventsSubscription.add(
      this.documentCounterService.receivedDocumentsObserver.subscribe(
        receivedDocuments => this.recievedDocuments = receivedDocuments
      )
    );

    this.eventsSubscription.add(
      this.documentCounterService.totalDocumentsObserver.subscribe(
        totalDocuments => this.totalDocuments = totalDocuments
      )
    );

    this.eventsSubscription.add(
      this.breadcrumbService.getStep.subscribe(step => this.step = step)
    );

    this.eventsSubscription.add(
      this.stateService.ootsRequestModelObserver.subscribe(uuid => { this.refreshOOTSRequestModel(uuid); })
    );

    this.eventsSubscription.add(
      this.stateService.getLanguage.subscribe(lang => {
        if (this.lang != lang) this.lang = lang;
      }));
  }
  
  @HostListener('document:visibilitychange', ['$event'])
  beforeContentLoaded(event: any): void {
    if (!document.hidden) {
      this.sessionTimerComponent.initTimer(this.uuid);
    }
  }

  ngOnInit(): void {
    const queryParams = this.route.snapshot.queryParams;
    const initialUuid = queryParams['uuid'] ?? this.cookieService.get('uuid') ?? '';

    this.handleInitialUuidAndCookies(initialUuid);

    this.route.queryParams.subscribe(params => {
      const uuid = params['uuid'] ?? this.cookieService.get('uuid') ?? '';
      if (!uuid) return;

      this.uuid = uuid;

      if (this.jwtUtilService.isLogged()) {
        this.isUserLogged = true;
        this.currentUser = this.jwtUtilService.getCurrentUser();

        const userIdentifier = this.extractUserIdentifierFromEidasUser(this.currentUser.personIdentifier);
        this.sessionService.findPreviousSession(this.uuid, userIdentifier).subscribe({
          next: session => {
            this.sessionData = {
              currentSession: this.uuid,
              previousSession: session.uuid,
              userIdentifier: session.userIdentifier,
              dir3Code: session.dir3Code,
              siaCode: session.siaCode,
              returnUrl: session.returnUrl,
              existsPrevious: session.existsPrevious
            };

            this.fetchOOTSRequests(this.uuid);
            this.sessionTimerComponent.initTimer(this.uuid);
          },
          error: e => console.error(e)
        });
      }
    });
  }

  handleInitialUuidAndCookies(initialUuid: string): void {
    this.setUuidIfPresent(initialUuid);
    this.clearCookiesIfFreshLoad();
  }

  setUuidIfPresent(initialUuid: string): void {
    if (!initialUuid) return;
    const oneHourFromNow = new Date(Date.now() + 1000 * 60 * 60);
    this.cookieService.set("uuid", initialUuid, { expires: oneHourFromNow, path: '/' });
    this.uuid = initialUuid;
  }

  clearCookiesIfFreshLoad(): void {
    const navigation = window.performance.getEntries()[0] as PerformanceNavigationTiming;
    const isReload = navigation.type === 'reload';
    if (isReload) return;
    if (!this.jwtUtilService.checkCookie('redirect')) return;
    if (this.jwtUtilService.getRedirect()) {
      this.jwtUtilService.saveRedirect('false');
      return;
    }

    this.expireSessionCookies([
      'token',
      'oots-request-status',
      'currentUser',
      'errorType',
      'isPreview'
    ]);
  }

  expireSessionCookies(cookies: string[]): void {
    cookies.forEach(cookie => {
      if (this.jwtUtilService.checkCookie(cookie)) {
        this.jwtUtilService.setExpired(cookie);
      }
    });
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  getFormattedPersonIdentifier(): string {
    return this.currentUser?.personIdentifier.length < 20 ? this.currentUser?.personIdentifier : this.currentUser?.personIdentifier.substring(0, 20) + "...";
  }

  agreementChange(event: any) {
    this.stateService.agreementAccepted = this.isChecked;
  }

  continuationTokenChange(event: any) {
    this.stateService.continuationToken = this.continuationToken;
  }

  checkPreviousProcessWithToken() {
    const user = this.jwtUtilService.getCurrentUser();
    const userIdentifier = this.extractUserIdentifierFromEidasUser(user.personIdentifier);
    this.findPreviousRequestByToken(userIdentifier, this.continuationToken);
  }

  fetchOOTSRequests(uuid: string) {
    this.getOOTSRequest(uuid).subscribe(
      {
        next: result => {
          this.ootsRequest = JSON.parse(result);
          if (this.ootsRequest != null) {
            this.loading = false;
            switch (this.ootsRequest.procedureCode) {
              case 'T1': this.procedureDescription = "Applying for a tertiary education study financing, such as study grants and loans from a public body or institution"; break;
              case 'T2': this.procedureDescription = "Submitting an initial application for admission to public tertiary education institution"; break;
              case 'T3': this.procedureDescription = "Requesting academic recognition of diplomas, certificates or other proof of studies or courses"; break;
              case 'V1': this.procedureDescription = "Registering a change of address"; break;
              case 'R1': this.procedureDescription = "Requesting proof of registration of birth"; break;
            }
            this.stateService.ootsRequest = this.ootsRequest;
            this.updateUserIdentifierIfNeeded(this.ootsRequest);
            this.ootsRequest.requirements.forEach(requirement => {
              this.requirementsList.push(requirement.title);
            });

            //update step
            this.ootsRequest.requirements.forEach(requirement => {
              requirement.evidences.forEach(evidence => {
                if (evidence.status === "RECEIVED") this.breadcrumbService.setStep(3);
              });
            });

          }
        },
        error: (e) => {
          console.error(e)
        },
      });
  }

  refreshOOTSRequestModel(uuid: string) {
    if (uuid) {
      this.documentCounterService.resetCounters();
      this.getOOTSRequest(uuid).subscribe(
        {
          next: result => {
            this.ootsRequest = JSON.parse(result);
            if (this.ootsRequest != null) this.stateService.ootsRequest = this.ootsRequest;
          },
          error: (e) => {
            console.error(e)
          },
        });
    }
  }

  getOOTSRequest(uuid: string): Observable<any> {
    return this.ootsService.getOOTSRequestByUUID(uuid, this.lang);
  }

  updateUserIdentifierIfNeeded(ootsRequest: OotsRequest) {
    // Update user identifier on out of session process
    if (!this.ootsRequest.inSession && this.ootsRequest.userIdentifier === null) {
      const user = this.jwtUtilService.getCurrentUser();
      ootsRequest.userIdentifier = this.extractUserIdentifierFromEidasUser(user.personIdentifier);
      this.ootsService.setUserIdentifier(ootsRequest).subscribe(
        {
          next: () => {
            this.stateService.ootsRequest = this.ootsRequest;
          },
          error: (e) => {
            console.error(e)
          }
        });
    }
  }

  findPreviousRequestByToken(userIdentifier: string, continuationToken: string) {
    this.ootsService.findPreviousRequestByToken(userIdentifier, continuationToken).subscribe(
      {
        next: result => {
          this.ootsRequest = JSON.parse(result);
          this.stateService.ootsRequest = this.ootsRequest;
        },
        error: (e) => {
          if (e.status == '404') this.tokenFound = false;
          if (e.status == '400') this.isRequestDraftStatus = false;
        }
      });
      this.loading = false;
  }

  goToLink(link: string) {
    window.open(link, "_blank");
  }

  private extractUserIdentifierFromEidasUser(ediasUser: string): string {
    const lastIndex = ediasUser.lastIndexOf('/');
    if (lastIndex !== -1) return ediasUser.substring(lastIndex + 1);
    else return ediasUser;
  }

  onInputChange(event: any) {
    this.tokenFound = true;
  }

  toggle() {
    const container = document.querySelector('.expandable-container');
    container?.classList.toggle('contract');
    const lnk = document.querySelector('.toggle-lnk');
    lnk!.textContent = container?.classList.contains('contract') ? '▼ Expandir' : '▲ Contraer';
  }

}

